import { useCallback, useMemo, useState, useRef, useEffect } from "react";

export type Updater<T> = T | ((prev: T) => T);

export function useSessionStorageState<T>(
  key: string,
  initialValue?: T | (() => T)
): [
  T | undefined,
  (next: Updater<T | undefined>) => void,
  { remove: () => void }
] {
  const read = useCallback((): T | undefined => {
    try {
      const raw = sessionStorage.getItem(key);
      return raw == null ? undefined : (JSON.parse(raw) as T);
    } catch {
      return undefined;
    }
  }, [key]);

  const initial = useMemo<T | undefined>(() => {
    const stored = read();
    if (stored !== undefined) return stored;
    return typeof initialValue === "function"
      ? (initialValue as () => T | undefined)()
      : initialValue;
  }, [read, initialValue]);

  const [state, setState] = useState<T | undefined>(initial);
  const prevKeyRef = useRef(key);

  useEffect(() => {
    // handle key change
    if (prevKeyRef.current !== key) {
      prevKeyRef.current = key;
    }
    try {
      if (state === undefined) {
        // don't write "undefined" as a string; clear the key instead
        sessionStorage.removeItem(key);
      } else {
        sessionStorage.setItem(key, JSON.stringify(state));
      }
    } catch {
      /* ignore quota/serialization errors */
    }
  }, [key, state]);

  const set = useCallback((next: Updater<T | undefined>) => {
    setState((prev) =>
      typeof next === "function"
        ? (next as (p: T | undefined) => T | undefined)(prev)
        : next
    );
  }, []);

  const remove = useCallback(() => {
    try {
      sessionStorage.removeItem(key);
    } finally {
      const fallback =
        typeof initialValue === "function"
          ? (initialValue as () => T | undefined)()
          : initialValue;
      setState(fallback);
    }
  }, [key, initialValue]);

  return [state, set, { remove }];
}
